import { Capacitor } from '@capacitor/core';

/**
 * 인증 추상화. Phase α 는 부팅 시 익명 로그인만 강제 — Apple/Google 사인인은
 * Settings 화면에서 사용자 트리거. Firestore CloudSave 는 익명 uid 로도 작동.
 *
 * 웹에서는 Mock — 모든 메소드가 localStorage 에 가짜 uid 저장. Phase β/γ 에서
 * 익명/소셜 머지 흐름 검증할 때 native 만 진실 소스.
 *
 * native 진입점 (NativeAuth) 은 src/services/FirebaseAuth.ts 로 분리되어
 * 동적 import 됨 — web 번들에 firebase-auth plugin 코드가 들어가지 않음.
 */
export interface AppUser {
  uid: string;
  isAnonymous: boolean;
  displayName: string | null;
  provider: 'anonymous' | 'apple.com' | 'google.com';
}

export interface AuthService {
  current(): Promise<AppUser | null>;
  signInAnonymous(): Promise<AppUser>;
  signInWithApple(): Promise<AppUser>;
  signInWithGoogle(): Promise<AppUser>;
  signOut(): Promise<void>;
}

const WEB_UID_KEY = 'yodajjom.webMockUid.v1';

const randomSuffix = () => Math.random().toString(36).slice(2, 12);

export class WebMockAuth implements AuthService {
  async current(): Promise<AppUser | null> {
    const uid = localStorage.getItem(WEB_UID_KEY);
    if (!uid) return null;
    return { uid, isAnonymous: true, displayName: null, provider: 'anonymous' };
  }
  async signInAnonymous(): Promise<AppUser> {
    let uid = localStorage.getItem(WEB_UID_KEY);
    if (!uid) {
      uid = 'web-' + randomSuffix();
      localStorage.setItem(WEB_UID_KEY, uid);
    }
    return { uid, isAnonymous: true, displayName: null, provider: 'anonymous' };
  }
  async signInWithApple(): Promise<AppUser> {
    const uid = 'web-apple-' + randomSuffix();
    localStorage.setItem(WEB_UID_KEY, uid);
    return { uid, isAnonymous: false, displayName: 'Apple User', provider: 'apple.com' };
  }
  async signInWithGoogle(): Promise<AppUser> {
    const uid = 'web-google-' + randomSuffix();
    localStorage.setItem(WEB_UID_KEY, uid);
    return { uid, isAnonymous: false, displayName: 'Google User', provider: 'google.com' };
  }
  async signOut(): Promise<void> {
    localStorage.removeItem(WEB_UID_KEY);
  }
}

let cached: Promise<AuthService> | null = null;

export function getAuth(): Promise<AuthService> {
  if (!cached) {
    cached = (async () => {
      if (Capacitor.isNativePlatform()) {
        const { NativeAuth } = await import('./FirebaseAuth');
        return new NativeAuth();
      }
      return new WebMockAuth();
    })().catch((err) => {
      cached = null;
      throw err;
    });
  }
  return cached;
}

/** 부팅 시 자동 호출 — 이미 로그인돼 있으면 그 유저, 아니면 익명 생성. */
export async function bootAnonymous(): Promise<AppUser> {
  const auth = await getAuth();
  const existing = await auth.current();
  if (existing) return existing;
  return auth.signInAnonymous();
}
